import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResidentAppActionTile,
  ResidentAppActionTiles,
} from '@pandiafi/components'

import { Contract, FeatureFlag } from '../../../common/validations'
import { MessagesActionTile } from './MessagesActionTile/MessagesActionTile'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import routePath from 'router/routePath'
import { useFeaturePermission } from 'components/Permissions/hooks/useFeaturePermission'

interface TaskLinksProps {
  featureFlags: FeatureFlag
  contract: Contract
  className?: string
}

export const TaskLinks: React.FC<TaskLinksProps> = ({
  featureFlags,
  contract,
  className,
}) => {
  const modules = useModuleConfig()
  const hasPermission = useFeaturePermission()
  const { t } = useTranslation(['common'])
  const {
    messagesPath,
    defectNoticePath,
    paymentHistoryPath,
    saunaReservationsPath,
  } = useContractPaths()

  // defect notice enabled unless contractTerminationOnly or explicitly false
  const defectNoticeEnabled =
    !modules.contractTerminationOnly && modules.defectNotice.enabled !== false
  const externalDefectUrl = defectNoticeEnabled
    ? modules.defectNotice.customDefectNoticeLinkUrl
    : undefined
  const contractModification = modules.contractModification ?? false

  return (
    <ResidentAppActionTiles
      aria-label={t('common:taskLinks.label')}
      className={className}
    >
      {!modules.contractTerminationOnly && modules.messaging && (
        <MessagesActionTile href={messagesPath} />
      )}
      {defectNoticeEnabled && (
        <ResidentAppActionTile
          icon="reparation"
          href={externalDefectUrl ?? defectNoticePath}
          external={!!externalDefectUrl}
        >
          {t('common:taskLinks.defectNotice')}
        </ResidentAppActionTile>
      )}
      {!modules.contractTerminationOnly && hasPermission('payments') && (
        <ResidentAppActionTile
          icon="euro-sign-circle"
          href={paymentHistoryPath}
          status={featureFlags.paymentInfo ? 'default' : 'upcoming'}
          statusLabel={
            featureFlags.paymentInfo ? undefined : t('common:comingSoon')
          }
        >
          {t('common:taskLinks.payments')}
        </ResidentAppActionTile>
      )}
      {!modules.contractTerminationOnly && modules.sauna.enabled && (
        <ResidentAppActionTile icon="sauna" href={saunaReservationsPath}>
          {t('common:taskLinks.saunaReservation')}
        </ResidentAppActionTile>
      )}
      {contractModification && hasPermission('contractChanges') && (
        <ResidentAppActionTile
          icon="group"
          href={routePath.residentChanges.replace(':contractId', contract.uuid)}
        >
          {t('common:taskLinks.residentChanges')}
        </ResidentAppActionTile>
      )}
      {hasPermission('contractChanges') && (
        <ResidentAppActionTile
          icon="moving"
          href={routePath.terminateContract.replace(
            ':contractId',
            contract.uuid
          )}
        >
          {t('common:taskLinks.moveNotice')}
        </ResidentAppActionTile>
      )}
    </ResidentAppActionTiles>
  )
}
